import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { isEmpty } from "lodash";
import TableContainer from "../../../../Components/Common/TableContainer";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkEmailDomainDNS,
  checkEmailDomainDNSSuccess,
  deleteEmailAccount,
  deleteEmailAccountSuccess,
  deleteEmailDomain,
  deleteEmailDomainSuccess,
  emailDomainList,
  getEmailAccountDetails,
  getEmailAccountList,
} from "../../../../store/CRMitEmailInbox/action";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import CreateEditSettingsEmailAccount from "../../../../Components/popUpComponents/CreateEditSettingsEmailAccount";
import { toast } from "react-toastify";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { Tooltip } from "@mui/material";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import CreateNewEmailDomain from "../../../../Components/popUpComponents/CreateNewEmailDomain";
import DnsRecordsPanel from "../../../../Components/Common/DnsRecordsPanel/DnsRecordsPanel";
import AppModal from "../../../../Components/Common/AppModal";

export default function TabEmailDomains() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const { emailAccountsListAPI, emailAccountsListLoading } = useSelector(
    (state) => ({
      emailAccountsListAPI:
        state.EmailInbound.emailDomainList?.emaildomain_list,
      emailAccountsListLoading: state.EmailInbound.emailDomainListLoading,
    })
  );

  useEffect(() => {
    dispatch(emailDomainList());
  }, []);

  /* Task 24333 — domain ownership verification.
     The backend already does the work (emaildomain/checkdns registers and
     checks the TXT records via Brevo) and the redux plumbing existed, but
     nothing on screen ever called it, so a domain could be added and then
     never verified. */
  const { checkDnsResponse, checkDnsLoading } = useSelector((state) => ({
    checkDnsResponse: state.EmailInbound.emailDomainCheckDNS,
    checkDnsLoading: state.EmailInbound.emailDomainCheckDNSLoading,
  }));
  const [verifyDomain, setVerifyDomain] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);

  const handleVerifyDomain = (row) => {
    setVerifyDomain(row);
    setVerifyResult(null);
    dispatch(checkEmailDomainDNS({ id: row?.Id }));
  };

  useEffect(() => {
    if (isEmpty(checkDnsResponse)) return;
    if (!verifyDomain) return;
    // A 200 with no payload means Brevo confirmed the records — anything else
    // is "still pending", and carries either the records to publish (409) or
    // the per-record progress flags (200).
    const records = checkDnsResponse?.data?.records;
    const verified =
      checkDnsResponse?.status === 200 && isEmpty(checkDnsResponse?.data);
    if (verified) {
      toast.success(checkDnsResponse?.message);
      dispatch(emailDomainList());
      setVerifyDomain(null);
    } else {
      setVerifyResult({
        message: checkDnsResponse?.message,
        records: records || [],
        progress: records ? null : checkDnsResponse?.data,
      });
    }
    dispatch(checkEmailDomainDNSSuccess([]));
  }, [checkDnsResponse]);

  const [copyToClipboard, setCopyToClipboard] = useState(null);
  useEffect(() => {
    const delay = setTimeout(() => {
      setCopyToClipboard(null);
    }, 1000);
    return () => {
      clearTimeout(delay);
    };
  }, [copyToClipboard]);

  const columns = useMemo(
    () => [
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (card) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                <span className={"like-href"}> #{card.row.original.Id}</span>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Domain",
        accessor: "DomainUrl",
        filterable: true,
        Cell: (card) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {card.row.original?.DomainUrl}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Status",
        accessor: "MasterEmailDomainStatus_Name",
        filterable: true,
        Cell: (card) => {
          // Status 2 is set by checkdns once Brevo confirms the records.
          const isVerified =
            String(card.row.original?.MasterEmailDomainStatus_Id) === "2";
          return (
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                <span
                  className={`badge ${
                    isVerified
                      ? "bg-success-subtle text-success"
                      : "bg-warning-subtle text-warning"
                  }`}
                >
                  {card.row.original?.MasterEmailDomainStatus_Name ||
                    (isVerified ? "Verified" : "Pending verification")}
                </span>
                {card.row.original?.DnsLastValidatedDateTime ? (
                  <div className="text-muted fs-12 mt-1">
                    Verified{" "}
                    {HandleDateTime(
                      card.row.original?.DnsLastValidatedDateTime
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Email Provider Id",
        accessor: "MasterEmailProvider_Id",
        filterable: true,
        Cell: (card) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1 ms-2 name">
                {card.row.original?.MasterEmailProvider_Id}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Action",
        Cell: (cellProps) => {
          return (
            <div className="d-flex align-items-center gap-2">
              {/* Task 24333 — check the ownership TXT records */}
              <button
                type="button"
                className="btn btn-sm btn-soft-primary"
                title="Check the DNS records and verify this domain"
                disabled={checkDnsLoading}
                onClick={() => handleVerifyDomain(cellProps.row.original)}
              >
                {checkDnsLoading &&
                verifyDomain?.Id === cellProps.row.original?.Id
                  ? "Checking…"
                  : "Verify"}
              </button>
              <UncontrolledDropdown>
                <DropdownToggle
                  className="btn btn-soft-info btn-icon fs-14"
                  type="button"
                  id="dropdownMenuButton1"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="fa-solid fa-ellipsis"></i>
                </DropdownToggle>
                <DropdownMenu>
                  <li>
                    <DropdownItem>
                      <div
                        className="remove-item-btn"
                        onClick={() => {
                          setSelectToDelete([cellProps.row.original?.Id]);
                          setModalDeleteEmailAccount(true);
                        }}
                      >
                        <div className={"job-settings-buttons"}>
                          <i className="ri-delete-bin-fill align-center text-muted me-2"></i>{" "}
                          <span> Delete</span>
                        </div>
                      </div>
                    </DropdownItem>
                  </li>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        },
      },
    ],
    [copyToClipboard, searchResults, checkDnsLoading, verifyDomain]
  );
  const [allowedSearchFilters, setAllowedSearchFilters] = useState([
    "Id",
    "DomainUrl",
    "MasterEmailDomainStatus_Name",
  ]);
  useEffect(() => {
    let filteredCustomerList = emailAccountsListAPI;

    const timer = setTimeout(() => {
      filteredCustomerList = filteredCustomerList?.filter((item) => {
        return allowedSearchFilters.some((key) => {
          return (
            item[key] &&
            item[key].toString().toLowerCase().includes(search?.toLowerCase())
          );
        });
      });

      setSearchResults(filteredCustomerList);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, emailAccountsListAPI]);

  const [modalCreateEditEmailAccount, setModalCreateEditEmailAccount] =
    useState(false);
  const toggleCreateEditEmailAccount = () => {
    setModalCreateEditEmailAccount(!modalCreateEditEmailAccount);
  };
  const [isEdit, setIsEdit] = useState(false);

  const handleCreateUpdateEmailAccount = (type, id) => {
    if (type === "create") {
      setIsEdit(false);
      toggleCreateEditEmailAccount();
    }
    if (type === "update") {
      setIsEdit(true);
      toggleCreateEditEmailAccount();
      dispatch(getEmailAccountDetails({ id }));
    }
  };

  const { emailAccountsDetailsAPI } = useSelector((state) => ({
    emailAccountsDetailsAPI:
      state.EmailInbound.emailDomainData?.emaildomain_data,
  }));
  const { emailAccountsDeleteAPI } = useSelector((state) => ({
    emailAccountsDeleteAPI: state.EmailInbound.emailDomainDelete,
  }));
  const [selectToDelete, setSelectToDelete] = useState([]);

  const handleDeleteEmailAccount = () => {
    dispatch(deleteEmailDomain(selectToDelete));
  };
  //Delete Email Account response
  useEffect(() => {
    if (isEmpty(emailAccountsDeleteAPI)) return;
    if (emailAccountsDeleteAPI?.status === 200) {
      toast.success(emailAccountsDeleteAPI.message);
      dispatch(emailDomainList());
      setModalDeleteEmailAccount(false);
      setSelectToDelete([]);
    } else {
      toast.error(emailAccountsDeleteAPI.message);
    }
    dispatch(deleteEmailDomainSuccess([]));
  }, [emailAccountsDeleteAPI]);
  const [modalDeleteEmailAccount, setModalDeleteEmailAccount] = useState(false);
  return (
    <>
      {modalDeleteEmailAccount && (
        <DeleteModal
          show={modalDeleteEmailAccount}
          onCloseClick={() => setModalDeleteEmailAccount(false)}
          onDeleteClick={handleDeleteEmailAccount}
          title={"Delete Email Domain"}
          description={"Are you sure you want to delete this email domain"}
        />
      )}
      {/* Task 24333 — what still needs publishing to prove domain ownership */}
      {verifyResult && (
        <AppModal
          open={!!verifyResult}
          onClose={() => {
            setVerifyResult(null);
            setVerifyDomain(null);
          }}
          title={`Verify ${verifyDomain?.DomainUrl || "domain"}`}
          headerAccent="indigo"
          size="lg"
          onSubmit={() => handleVerifyDomain(verifyDomain)}
          submitLabel={checkDnsLoading ? "Checking…" : "Check again"}
          submitColor="success"
        >
          <p className="mb-2">{verifyResult?.message}</p>
          {!isEmpty(verifyResult?.records) ? (
            <DnsRecordsPanel records={verifyResult.records} showStatus />
          ) : null}
          {verifyResult?.progress ? (
            <ul className="mb-0 mt-2 text-muted">
              <li>
                DKIM record:{" "}
                {verifyResult.progress?.dkim_ok ? "found" : "not found yet"}
              </li>
              <li>
                Ownership (TXT) record:{" "}
                {verifyResult.progress?.brevo_ok ? "found" : "not found yet"}
              </li>
              <li>
                DMARC record:{" "}
                {verifyResult.progress?.dmarc_ok ? "found" : "not found yet"}
              </li>
            </ul>
          ) : null}
        </AppModal>
      )}
      {modalCreateEditEmailAccount && (
        // todo
        <CreateNewEmailDomain
          modal={modalCreateEditEmailAccount}
          setModal={setModalCreateEditEmailAccount}
          toggle={toggleCreateEditEmailAccount}
          isEdit={isEdit}
          updateData={emailAccountsDetailsAPI}
        />
      )}

      <Row>
        <Col lg={12}>
          <Card id="customersList">
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <Col sm={3}>
                  <div className="search-box">
                    <Input
                      // create search with jobs List Data
                      type="search"
                      className="form-control search"
                      placeholder="Search for..."
                      onChange={(e) => setSearch(e.target.value)}
                      value={search}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
                <div className="col-sm-auto ms-auto">
                  <div className="hstack gap-2">
                    <button
                      title={"Add New Email Account"}
                      className="btn btn-success"
                      onClick={() => {
                        handleCreateUpdateEmailAccount("create");
                      }}
                    >
                      <i className="ri-add-line align-bottom me-1"></i>
                      Email Domain
                    </button>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <div>
                {emailAccountsListLoading ? (
                  <LoadingSpinner />
                ) : (
                  <div>
                    {!isEmpty(searchResults) ? (
                      <TableContainer
                        columns={columns}
                        data={searchResults || []}
                        isGlobalFilter={false}
                        isAddUserList={false}
                        customPageSize={10}
                        className="custom-header-css"
                        divClass="table-responsive table-card mb-0"
                        tableClass="align-middle table-wrap"
                        theadClass="text-muted"
                        // handleLeadClick={handleLeadClicks}
                        isLeadsFilter={false}
                      />
                    ) : (
                      <div>
                        {!isEmpty(emailAccountsListAPI) ? (
                          <NoDataContent
                            titleText="No domains found"
                            paragraphText={
                              "Please try with another search keyword."
                            }
                          />
                        ) : (
                          <NoDataContent
                            titleText="No domains found"
                            paragraphText={"Email domains list is empty."}
                            // todo once create account is ready add button to create new email account
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
